import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../../components/ReportModall.css";
import "../DashboardSkeleton.css";
import { useNoteReports, useNotesListActions } from "../../hooks/useNotesFeedback";

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3.25 3.25L12.75 12.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12.75 3.25L3.25 12.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const FeedbackModalShimmer = () => (
  <>
    {Array.from({ length: 3 }).map((_, index) => (
      <article className="report-card" key={`report-shimmer-${index}`}>
        <div className="report-card__top">
          <div className="report-card__heading">
            <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-index" />
            <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-title" />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--icon report-modal__shimmer-action" />
        </div>
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line" />
        <span className="dashboard-skeleton dashboard-skeleton--line report-modal__shimmer-line report-modal__shimmer-line--short" />
      </article>
    ))}
  </>
);

const NotesReportModal = ({ open, onClose, note = null, notesQueryParams = {} }) => {
  const [reportText, setReportText] = useState("");
  const noteId = Number(note?.id || 0);
  const reportsQuery = useNoteReports(noteId, {
    enabled: open && Boolean(noteId),
  });
  const { createReport, deleteReport } = useNotesListActions(notesQueryParams);

  const reportsLoading = (reportsQuery.isLoading || reportsQuery.isFetching) && (!reportsQuery.data || reportsQuery.isPlaceholderData);

  useEffect(() => {
    if (!open) {
      setReportText("");
    }
  }, [open, noteId]);

  if (!open) return null;

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    onClose?.();
  };

  const handleSave = async () => {
    if (!reportText.trim()) return;

    await createReport.mutateAsync({
      notes_id: noteId,
      message: reportText.trim(),
    });
    setReportText("");
  };

  const handleDeleteReport = async (item) => {
    const reportId = Number(item?.id || 0);
    if (!reportId || deleteReport.isPending) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Report?",
      text: "This report will be removed permanently for this note.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53935",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteReport.mutateAsync({
        report_id: reportId,
        notes_id: noteId,
      });

      await Swal.fire({
        icon: "success",
        title: "Report Deleted",
        text: "Your report has been removed successfully.",
      });
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Unable to Delete Report",
        text: error?.response?.data?.message || error?.message || "Please try again.",
      });
    }
  };

  return (
    <div className="report-modal-overlay" role="presentation" onClick={handleClose}>
      <div className="report-modal" role="dialog" aria-modal="true" aria-label="Your Report(s)" onClick={(event) => event.stopPropagation()}>
        <span className="report-modal__corner report-modal__corner--top" aria-hidden="true" />
        <span className="report-modal__corner report-modal__corner--bottom" aria-hidden="true" />
        <button type="button" className="report-modal__close" onClick={handleClose} aria-label="Close report modal">
          <CloseIcon />
        </button>

        <div className="report-modal__header">
          <div className="report-modal__title-group">
            <div className="report-modal__icon" aria-hidden="true">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <h3 className="report-modal__title">Your Report(s)</h3>
          </div>
        </div>

        <div className="report-modal__body">
          <div className="report-modal__list" aria-label="Reported notes">
            {reportsLoading ? <FeedbackModalShimmer /> : null}
            {!reportsLoading && reportsQuery.error ? (
              <div className="choose-state__status choose-state__status--error">
                {reportsQuery.error?.response?.data?.message || reportsQuery.error?.message || "Unable to load reports."}
              </div>
            ) : null}
            {!reportsLoading && !reportsQuery.error && !(reportsQuery.data?.items || []).length ? (
              <div className="choose-state__status">No reports available.</div>
            ) : null}
            {(reportsQuery.data?.items || []).map((item, index) => (
              <article className="report-card" key={item.id}>
                <div className="report-card__top">
                  <div className="report-card__heading">
                    <span className="report-card__index">{String(index + 1).padStart(2, "0")}</span>
                    <h4 className="report-card__title">Your Query</h4>
                  </div>
                  <button type="button" className="report-card__dismiss" onClick={() => handleDeleteReport(item)} disabled={deleteReport.isPending} aria-label="Delete report">
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                </div>
                <p className="report-card__text">{item.description}</p>
                {item.replyDescription ? (
                  <section className="report-card__reply">
                    <h5 className="report-card__reply-title">Admin Reply</h5>
                    <div className="report-card__reply-body">
                      <p className="report-card__reply-text">{item.replyDescription}</p>
                    </div>
                  </section>
                ) : null}
              </article>
            ))}
          </div>

          <div className="report-modal__compose">
            <div className="report-modal__compose-label">
              <i className="bi bi-file-earmark-text"></i>
              <span>Report This Note</span>
            </div>
            <textarea className="report-modal__textarea" placeholder="Report This Note" value={reportText} onChange={(event) => setReportText(event.target.value)} />
          </div>
        </div>

        <div className="report-modal__footer">
          <button type="button" className="report-modal__save" onClick={handleSave} disabled={!reportText.trim() || createReport.isPending}>
            {createReport.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotesReportModal;
